// Where the planets are at a given moment, from JPL's approximate Keplerian elements
// (Standish, "Keplerian Elements for Approximate Positions of the Major Planets", valid
// 1800–2050): good to a few arcminutes, far finer than a pixel of the hero's sky.

const DEG = Math.PI / 180;
const OBLIQUITY = 23.43928 * DEG;

// a (au), e, I, L, ϖ, Ω (deg) at J2000, each followed by its rate per Julian century
type Elements = [number, number, number, number, number, number, number, number, number, number, number, number];

const EARTH: Elements = [1.00000261, 0.00000562, 0.01671123, -0.00004392, -0.00001531, -0.01294668, 100.46457166, 35999.37244981, 102.93768193, 0.32327364, 0, 0];

// How each globe looks up close: banded gas giant, ringed, plain disc, or rocky.
export type PlanetLook = 'banded' | 'ringed' | 'plain' | 'rocky';

type Planet = {
  id: string;
  name: string;
  mag: number;
  elements: Elements;
  radius: number; // equatorial, km
  pole: [number, number]; // IAU north pole (ra, dec), J2000
  look: PlanetLook;
  rings?: [number, number]; // inner and outer edge, km (Saturn's C ring to A ring)
  fact: string;
};

export const PLANETS: Planet[] = [
  { id: 'mercury', name: 'Mercurio', mag: 0, radius: 2440, pole: [281.01, 61.42], look: 'rocky', fact: 'El planeta más cercano al Sol: allá un año dura 88 días.', elements: [0.38709927, 0.00000037, 0.20563593, 0.00001906, 7.00497902, -0.00594749, 252.2503235, 149472.67411175, 77.45779628, 0.16047689, 48.33076593, -0.12534081] },
  { id: 'venus', name: 'Venus', mag: -4.2, radius: 6052, pole: [272.76, 67.16], look: 'plain', fact: 'Del tamaño de la Tierra, bajo nubes de ácido sulfúrico: su superficie pasa los 460 °C.', elements: [0.72333566, 0.0000039, 0.00677672, -0.00004107, 3.39467605, -0.0007889, 181.9790995, 58517.81538729, 131.60246718, 0.00268329, 76.67984255, -0.27769418] },
  { id: 'mars', name: 'Marte', mag: 1, radius: 3396, pole: [317.68, 52.89], look: 'rocky', fact: 'Tiene el volcán más alto del sistema solar, el Monte Olimpo: casi tres veces el Everest.', elements: [1.52371034, 0.00001847, 0.0933941, 0.00007882, 1.84969142, -0.00813131, -4.55343205, 19140.30268499, -23.94362959, 0.44441088, 49.55953891, -0.29257343] },
  { id: 'jupiter', name: 'Júpiter', mag: -2.4, radius: 71492, pole: [268.06, 64.5], look: 'banded', fact: 'Cabrían más de mil Tierras adentro. Sus bandas son nubes de amoníaco arrastradas por vientos de cientos de km/h.', elements: [5.202887, -0.00011607, 0.04838624, -0.00013253, 1.30439695, -0.00183714, 34.39644051, 3034.74612775, 14.72847983, 0.21252668, 100.47390909, 0.20469106] },
  { id: 'saturn', name: 'Saturno', mag: 0.8, radius: 60268, pole: [40.59, 83.54], look: 'ringed', rings: [74658, 136775], fact: 'En marzo de 2025 sus anillos quedaron de canto frente a la Tierra; ahora recién empiezan a abrirse. Son de hielo, y casi en ningún lado más gruesos que un kilómetro.', elements: [9.53667594, -0.0012506, 0.05386179, -0.00050991, 2.48599187, 0.00193609, 49.95424423, 1222.49362201, 92.59887831, -0.41897216, 113.66242448, -0.28867794] },
  { id: 'uranus', name: 'Urano', mag: 5.6, radius: 25559, pole: [257.31, -15.18], look: 'plain', fact: 'Gira tumbado: su eje está inclinado 98°. Fue el primer planeta descubierto con telescopio, en 1781.', elements: [19.18916464, -0.00196176, 0.04725744, -0.00004397, 0.77263783, -0.00242939, 313.23810451, 428.48202785, 170.9542763, 0.40805281, 74.01692503, 0.04240589] },
  { id: 'neptune', name: 'Neptuno', mag: 7.8, radius: 24764, pole: [299.36, 43.46], look: 'plain', fact: 'Invisible a simple vista. En 1846 lo encontraron primero en los cálculos, por cómo desviaba a Urano, y recién después en el cielo.', elements: [30.06992276, 0.00026291, 0.00859048, 0.00005105, 1.77004347, 0.00035372, -55.12002969, 218.45945325, 44.96476227, -0.32241464, 131.78422574, -0.00508664] },
];

const AU_KM = 149_597_870.7;

// Heliocentric ecliptic position (au) `centuries` after J2000.
function heliocentric(q: Elements, centuries: number): [number, number, number] {
  const at = (i: number) => q[i] + q[i + 1] * centuries;
  const a = at(0);
  const e = at(2);
  const inc = at(4) * DEG;
  const lon = at(6);
  const peri = at(8);
  const node = at(10) * DEG;
  const arg = peri * DEG - node;
  const mean = ((lon - peri) % 360) * DEG;
  let ecc = mean;
  for (let i = 0; i < 8; i++) ecc -= (ecc - e * Math.sin(ecc) - mean) / (1 - e * Math.cos(ecc));
  const x = a * (Math.cos(ecc) - e);
  const y = a * Math.sqrt(1 - e * e) * Math.sin(ecc);
  const [cw, sw, cn, sn, ci, si] = [Math.cos(arg), Math.sin(arg), Math.cos(node), Math.sin(node), Math.cos(inc), Math.sin(inc)];
  return [
    (cw * cn - sw * sn * ci) * x + (-sw * cn - cw * sn * ci) * y,
    (cw * sn + sw * cn * ci) * x + (-sw * sn + cw * cn * ci) * y,
    sw * si * x + cw * si * y,
  ];
}

// Every planet's geocentric (ra, dec) in degrees at `ms` (Unix milliseconds).
export function planetPositions(ms: number) {
  const centuries = (ms / 86_400_000 + 2440587.5 - 2451545) / 36525;
  const earth = heliocentric(EARTH, centuries);
  return PLANETS.map((planet) => {
    const p = heliocentric(planet.elements, centuries);
    const x = p[0] - earth[0];
    const y = p[1] - earth[1];
    const z = p[2] - earth[2];
    const ye = y * Math.cos(OBLIQUITY) - z * Math.sin(OBLIQUITY);
    const ze = y * Math.sin(OBLIQUITY) + z * Math.cos(OBLIQUITY);
    const ra = (Math.atan2(ye, x) / DEG + 360) % 360;
    const dec = Math.atan2(ze, Math.hypot(x, ye)) / DEG;
    const distance = Math.hypot(x, y, z); // au from the Earth
    // How the globe faces us: the latitude on it under the Earth (B, also how far Saturn's
    // rings are open), and the angle of its north pole on the sky from north through east (P).
    const [pa, pd] = [planet.pole[0] * DEG, planet.pole[1] * DEG];
    const [a, d] = [ra * DEG, dec * DEG];
    const toward = Math.sin(pd) * Math.sin(d) + Math.cos(pd) * Math.cos(d) * Math.cos(pa - a);
    return {
      id: planet.id,
      name: planet.name,
      mag: planet.mag,
      look: planet.look,
      fact: planet.fact,
      ra,
      dec,
      distance,
      radiusKm: planet.radius,
      rings: planet.rings,
      angularRadius: Math.asin(planet.radius / (distance * AU_KM)) / DEG, // deg
      tilt: -Math.asin(toward) / DEG, // B, deg
      poleAngle: Math.atan2(Math.cos(pd) * Math.sin(pa - a), Math.sin(pd) * Math.cos(d) - Math.cos(pd) * Math.sin(d) * Math.cos(pa - a)) / DEG, // P, deg
    };
  });
}
